import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { DollarSign, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminProcessSalary() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [processing, setProcessing] = useState(false);
  const [overrideLateDeduction, setOverrideLateDeduction] = useState(false);
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmployees();
    fetchSalaryRecords();
  }, []);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.filter(u => u.role === 'employee'));
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalaryRecords = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/salary/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalaryRecords(response.data);
    } catch (error) {
      toast.error('Failed to load salary records');
    }
  };

  const handleProcessSalary = async () => {
    if (!selectedEmployee) {
      toast.error('Please select an employee');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/salary/process/${selectedEmployee}?month=${month}&year=${year}&override_late_deduction=${overrideLateDeduction}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Salary processed successfully');
      fetchSalaryRecords();
      setSelectedEmployee('');
      setOverrideLateDeduction(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process salary');
    } finally {
      setProcessing(false);
    }
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  return (
    <Layout>
      <div className="space-y-8" data-testid="admin-process-salary-page">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Process Salary</h1>
          <p className="text-muted-foreground mt-2">Calculate and process employee salaries</p>
        </div>

        <Card data-testid="process-salary-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <DollarSign className="mr-2 h-5 w-5" />
              Process Monthly Salary
            </CardTitle>
            <CardDescription>
              Select employee and month to process salary based on attendance
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Employee</Label>
                  <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                    <SelectTrigger data-testid="select-employee">
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.full_name} - {emp.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Month</Label>
                  <Select value={month.toString()} onValueChange={(val) => setMonth(parseInt(val))}>
                    <SelectTrigger data-testid="select-month">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((m) => (
                        <SelectItem key={m.value} value={m.value.toString()}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Year</Label>
                  <Input
                    type="number"
                    value={year}
                    onChange={(e) => setYear(parseInt(e.target.value))}
                    data-testid="input-year"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2 p-4 bg-secondary rounded-lg">
                <input
                  type="checkbox"
                  id="override-late"
                  checked={overrideLateDeduction}
                  onChange={(e) => setOverrideLateDeduction(e.target.checked)}
                  className="rounded border-gray-300"
                  data-testid="checkbox-override-late"
                />
                <Label htmlFor="override-late" className="cursor-pointer">
                  Override late deduction (Skip half-day deduction for late arrivals)
                </Label>
              </div>

              <Button
                onClick={handleProcessSalary}
                disabled={processing || !selectedEmployee}
                className="w-full"
                data-testid="process-button"
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                {processing ? 'Processing...' : 'Process Salary'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="salary-records-card">
          <CardHeader>
            <CardTitle>Processed Salaries</CardTitle>
            <CardDescription>History of processed salary records</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : salaryRecords.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No salary records found
              </div>
            ) : (
              <div className="w-full overflow-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Month</TableHead>
                      <TableHead>Base Salary</TableHead>
                      <TableHead>Present</TableHead>
                      <TableHead>Absent</TableHead>
                      <TableHead>Late</TableHead>
                      <TableHead>Deductions</TableHead>
                      <TableHead>Final Salary</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salaryRecords.map((record) => (
                      <TableRow key={record.id} data-testid="salary-record-row">
                        <TableCell className="font-medium">{record.employee_name}</TableCell>
                        <TableCell>{record.month}</TableCell>
                        <TableCell>₹{record.base_salary.toFixed(2)}</TableCell>
                        <TableCell>{record.days_present}</TableCell>
                        <TableCell>{record.days_absent}</TableCell>
                        <TableCell>
                          {record.days_late}
                          {record.late_deduction_overridden && (
                            <span className="ml-2 text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">
                              Override
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-red-600">₹{record.deductions.toFixed(2)}</TableCell>
                        <TableCell className="font-bold">₹{record.final_salary.toFixed(2)}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                            {record.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
